import { useState, useCallback, useRef } from 'react';
import { Dealer } from '@/types/dealer';
import { DealerFilterState } from '../types';
import { InfiniteScrollState } from './types';

export const useInfiniteScrollState = (initialFilters: Partial<DealerFilterState> = {}) => {
  // Ref to prevent re-render loops
  const lastUpdateRef = useRef<number>(0);
  const DEBOUNCE_MS = 100;
  
  // State management with proper defaults
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  
  // Enhanced setDealers with debouncing to prevent loops
  const enhancedSetDealers = useCallback((dealersOrFunction: Dealer[] | ((prev: Dealer[]) => Dealer[])) => {
    const now = Date.now();
    if (now - lastUpdateRef.current < DEBOUNCE_MS) {
      return;
    }
    lastUpdateRef.current = now;
    
    if (typeof dealersOrFunction === 'function') {
      setDealers(prev => {
        const newDealers = dealersOrFunction(prev);
        return newDealers;
      });
    } else {
      setDealers(dealersOrFunction);
    }
  }, []);
  
  // Enhanced setTotalCount with logging
  const enhancedSetTotalCount = useCallback((count: number) => {
    setTotalCount(count);
  }, []);
  
  // Enhanced setHasMore with logging
  const enhancedSetHasMore = useCallback((hasMore: boolean) => {
    setHasMore(hasMore);
  }, []);
  
  // Enhanced setError with logging
  const enhancedSetError = useCallback((error: string | null) => {
    setError(error);
  }, []);
  
  // Filters state with proper defaults to prevent undefined
  const [filters, setFilters] = useState<DealerFilterState>(() => ({
    search: "",
    range: [0, 5] as [number, number],
    filterVerified: false,
    filterActivated: false,
    sortOrder: "distance",
    distanceRange: 0,
    ...initialFilters
  }));

  // Reset pagination when filters change
  const resetPagination = useCallback(() => {
    setCurrentPage(0);
    enhancedSetDealers([]);
    enhancedSetHasMore(true);
    enhancedSetError(null);
  }, [enhancedSetDealers, enhancedSetHasMore, enhancedSetError]);

  // Update filters and reset pagination with proper state management
  const updateFilters = useCallback((newFilters: Partial<DealerFilterState>) => {
    setFilters(prev => {
      const updated = { ...prev, ...newFilters };
      return updated;
    });
    resetPagination();
  }, [resetPagination]);

  const state: InfiniteScrollState = {
    dealers,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    currentPage,
    totalCount
  };

  const actions = {
    setDealers: enhancedSetDealers,
    setIsLoading,
    setIsLoadingMore,
    setHasMore: enhancedSetHasMore,
    setError: enhancedSetError,
    setCurrentPage,
    setTotalCount: enhancedSetTotalCount,
    resetPagination,
    updateFilters,
    filters // Always return a valid filters object
  };

  return { state, actions };
};
